import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../../context/BrandingContext';
import './PaywallPage.css';

const MONTHLY_URL = 'https://pay.stackbd.com/hs/payments/fF9pwhNhrQ2b';
const ANNUAL_URL = 'https://pay.stackbd.com/hs/payments/pmqXspKMK';

const POLL_INTERVAL_MS = 60_000;
const MAX_POLLS = 10;

const PaywallPage = ({ auth }) => {
  const { t } = useTranslation(['paywall', 'common']);
  const [pollCount, setPollCount] = useState(0);
  const [showRefresh, setShowRefresh] = useState(false);
  const intervalRef = useRef(null);
  const { branding } = useBranding();

  const email = auth.user?.profile?.email || '';
  const appName = branding?.app_name || 'Stackbd';
  const supportEmail = branding?.support_email || 'contact@stackbd.com';

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      try {
        await auth.signinSilent();
      } catch (e) {
        // retry next interval
      }
      setPollCount(prev => {
        const next = prev + 1;
        if (next >= MAX_POLLS) {
          setShowRefresh(true);
          clearInterval(intervalRef.current);
        }
        return next;
      });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [auth]);

  const buildUrl = (base) => {
    const url = new URL(base);
    url.searchParams.set('referrer', 'PAYMENT_LINK');
    if (email) url.searchParams.set('email', email);
    return url.toString();
  };

  const handleLogout = () => {
    auth.signoutRedirect({
      extraQueryParams: {
        client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
        logout_uri: process.env.REACT_APP_LOGIN_REDIRECT_URL,
      },
    });
  };

  return (
    <div className="paywall-container" data-testid="paywall-page">
      <div className="paywall-content">
        <h1 className="paywall-heading" data-testid="paywall-heading">
          {t('heading', { appName, defaultValue: `Get started with ${appName}` })}
        </h1>
        <p className="paywall-subheading">
          {t('subheading')}
        </p>

        {email && (
          <div className="paywall-email-warning">
            {t('emailWarning', { email })}
          </div>
        )}

        <div className="paywall-cards">
          {/* Monthly */}
          <div className="paywall-card">
            <div className="paywall-plan-name">{t('plans.monthly.name')}</div>
            <div className="paywall-price">
              {t('plans.monthly.price')}<span>{t('plans.monthly.period')}</span>
            </div>
            <div className="paywall-price-note">{t('plans.monthly.priceNote')}</div>
            <ul className="paywall-features">
              {(t('plans.monthly.features', { returnObjects: true }) || []).map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            <a
              href={buildUrl(MONTHLY_URL)}
              target="_blank"
              rel="noopener noreferrer"
              className="paywall-cta"
              data-testid="paywall-cta"
            >
              {t('cta')}
            </a>
          </div>

          {/* Annual */}
          <div className="paywall-card featured">
            <div className="paywall-badge">{t('plans.annual.saveBadge')}</div>
            <div className="paywall-plan-name">{t('plans.annual.name')}</div>
            <div className="paywall-price">
              {t('plans.annual.price')}<span>{t('plans.annual.period')}</span>
            </div>
            <div className="paywall-price-note">{t('plans.annual.priceNote')}</div>
            <ul className="paywall-features">
              {(t('plans.annual.features', { returnObjects: true }) || []).map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
            <a
              href={buildUrl(ANNUAL_URL)}
              target="_blank"
              rel="noopener noreferrer"
              className="paywall-cta"
              data-testid="paywall-cta"
            >
              {t('cta')}
            </a>
          </div>
        </div>

        <div className="paywall-status">
          {!showRefresh ? (
            <>
              <div className="paywall-spinner" />
              <span>{t('status.checking')}</span>
            </>
          ) : (
            <div>
              <span>{t('status.waitingRefresh')}</span>
              <br />
              <button
                className="paywall-refresh-btn"
                onClick={() => window.location.reload()}
              >
                {t('status.refresh')}
              </button>
            </div>
          )}
        </div>

        <div className="paywall-footer">
          {email && <span className="paywall-user-email">{email}</span>}
          <button className="paywall-logout" onClick={handleLogout}>
            {t('logout')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaywallPage;
